// App-level wiring: cart drawer, add-to-cart hijack, marquee, UX polish
(function () {
  const drawerMount = document.getElementById('cart-drawer-mount');

  // ── Sun ray add-to-cart animation ─────────────────────────────────────────
  function showSunray(cb) {
    const overlay = document.createElement('div');
    overlay.className = 'sunray-overlay';
    overlay.innerHTML = '<div class="sunray-burst"></div>';
    document.body.appendChild(overlay);

    // Trigger transitions on next frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => overlay.classList.add('sunray-active'));
    });

    setTimeout(() => {
      overlay.classList.add('sunray-out');
      setTimeout(() => {
        overlay.remove();
        if (cb) cb();
      }, 400);
    }, 750);
  }

  // ── Cart drawer ────────────────────────────────────────────────────────────
  async function renderCartDrawer() {
    const cart = await ShopifyCart.get();
    const FREE = window.theme.settings.freeShippingThreshold || 6000;
    const pct = Math.min(100, (cart.total_price / FREE) * 100);
    const remaining = Math.max(0, FREE - cart.total_price);

    // Fetch product recommendations (items not already in cart)
    let recHtml = '';
    if (cart.item_count > 0) {
      try {
        const inCart = new Set(cart.items.map(i => i.product_id));
        const resp = await fetch('/collections/all-products/products.json?limit=8');
        const data = await resp.json();
        const recs = (data.products || []).filter(p => !inCart.has(p.id)).slice(0, 3);
        if (recs.length > 0) {
          recHtml = `
            <div class="cart-recs">
              <p class="cart-recs-label">You might also like</p>
              <div class="cart-recs-row">
                ${recs.map(p => {
                  const img = p.images && p.images[0] ? p.images[0].src : '';
                  const price = p.variants && p.variants[0] ? parseFloat(p.variants[0].price) * 100 : 0;
                  return `<a href="/products/${p.handle}" class="cart-rec-item" data-cart-close>
                    ${img ? `<img src="${img}" alt="${p.title}">` : '<span style="font-size:24px;">🍵</span>'}
                    <span class="rec-title">${p.title}</span>
                    <span class="rec-price">${ShopifyCart.formatMoney(price)}</span>
                  </a>`;
                }).join('')}
              </div>
            </div>`;
        }
      } catch (e) { /* recommendations are non-critical */ }
    }

    drawerMount.innerHTML = `
      <div class="overlay-backdrop" data-cart-close></div>
      <aside class="cart-drawer" role="dialog" aria-label="Shopping cart">
        <div class="cart-head">
          <h3>Your bowl <span class="kanji" style="font-size:18px;color:var(--forest-deep);">かご</span></h3>
          <button class="cart-close" data-cart-close aria-label="Close">✕</button>
        </div>
        ${cart.item_count === 0 ? `
          <div class="cart-empty">
            <div class="big">空</div>
            <h3 style="font-family:var(--sans);font-size:22px;font-weight:800;color:var(--forest-deep);margin:0;">Empty bowl</h3>
            <p style="font-size:14px;color:var(--forest);margin:0;max-width:260px;">Add a sachet or two — your future morning self will thank you.</p>
            <button class="btn btn-yolk" data-cart-close>Browse the sachets →</button>
          </div>
        ` : `
          <div class="cart-body">
            ${cart.total_price < FREE ? `
              <div style="padding:6px 4px;">
                <div style="font-size:13px;margin-bottom:6px;"><strong>${ShopifyCart.formatMoney(remaining)}</strong> away from free shipping</div>
                <div class="free-ship-bar"><span style="width:${pct}%"></span></div>
              </div>` : `
              <div style="padding:6px 4px;font-size:13px;color:var(--forest);">🎉 You unlocked free shipping</div>
            `}
            ${cart.items.map((it, i) => `
              <div class="cart-item" data-line="${i + 1}">
                <div class="cart-item-art">
                  ${it.image
                    ? `<img src="${it.image}" alt="" style="width:100%;height:100%;object-fit:contain;">`
                    : '<span style="font-size:28px;">🍵</span>'}
                </div>
                <div class="cart-item-info">
                  <h4>${it.product_title}</h4>
                  <p>${it.variant_title || ''}</p>
                  <div class="cart-qty">
                    <button data-decr>−</button>
                    <span class="qval">${it.quantity}</span>
                    <button data-incr>+</button>
                  </div>
                </div>
                <div class="price">${ShopifyCart.formatMoney(it.final_line_price)}</div>
              </div>`).join('')}
            ${recHtml}
          </div>
          <div class="cart-foot">
            <div class="tot"><span>Subtotal</span><span>${ShopifyCart.formatMoney(cart.total_price)}</span></div>
            <a href="/checkout" class="btn" style="justify-content:center;">Checkout →</a>
            <div class="free-ship-msg" style="text-align:center;margin-top:2px;">Ships cold · 2–3 days · 30-day taste guarantee</div>
          </div>
        `}
      </aside>`;

    drawerMount.querySelectorAll('[data-cart-close]').forEach(el => el.addEventListener('click', closeDrawer));

    // Fix stale closure: read live quantity from the DOM, not the closure value
    drawerMount.querySelectorAll('[data-line]').forEach(row => {
      const line = parseInt(row.dataset.line, 10);
      row.querySelector('[data-decr]').addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        if (btn.disabled) return;
        btn.disabled = true;
        const qvalEl = row.querySelector('.qval');
        const currentQty = parseInt(qvalEl.textContent, 10);
        const nextQty = currentQty - 1;
        qvalEl.textContent = nextQty;
        try {
          await ShopifyCart.change(line, nextQty);
        } catch (_) {}
        await renderCartDrawer();
        updateCartCount();
      });
      row.querySelector('[data-incr]').addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        if (btn.disabled) return;
        btn.disabled = true;
        const qvalEl = row.querySelector('.qval');
        const currentQty = parseInt(qvalEl.textContent, 10);
        const nextQty = currentQty + 1;
        qvalEl.textContent = nextQty;
        try {
          await ShopifyCart.change(line, nextQty);
        } catch (_) {}
        await renderCartDrawer();
        updateCartCount();
      });
    });
  }

  function closeDrawer() { drawerMount.innerHTML = ''; }

  async function updateCartCount() {
    const cart = await ShopifyCart.get();
    document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = cart.item_count);
  }

  // Open cart triggers
  document.querySelectorAll('[data-open-cart]').forEach(b =>
    b.addEventListener('click', (e) => { e.preventDefault(); renderCartDrawer(); }));
  window.addEventListener('cart:open', renderCartDrawer);

  // Open newsletter modal triggers (hero "Stay in the Loop" → footer modal)
  document.querySelectorAll('[data-open-newsletter]').forEach(b =>
    b.addEventListener('click', (e) => {
      e.preventDefault();
      const trigger = document.querySelector('[data-newsletter-open]');
      if (trigger) trigger.click();
    }));

  // Hijack add-to-cart forms — Ajax + sunray animation
  document.querySelectorAll('[data-product-add-form], [data-pdp-form]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const id = parseInt(fd.get('id'), 10);
      const qty = parseInt(fd.get('quantity') || '1', 10);
      try {
        await ShopifyCart.add(id, qty);
        showSunray(() => { renderCartDrawer(); updateCartCount(); });
      } catch (err) {
        console.error('add to cart failed', err);
      }
    });
  });

  updateCartCount();
})();
